import { createStateMachine } from "../helpers/useStateMachine";

export const questions = createStateMachine({
  start: "HEADLESS",
  states: {
    HEADLESS: {
      id: "HEADLESS",
      value: "Are you looking to connect your Shop with a Shopify store?",
      transitions: [
        {
          value: "Yes",
          nextState: "SHOPIFY",
        },
        {
          value: "No",
          nextState: "PURPOSE",
          bidirectional: true,
        },
      ],
    },
    PURPOSE: {
      id: "PURPOSE",
      value: "What sentence best describes your reason for creating a Shop?",
      transitions: [
        {
          value:
            "I'm creating a Shop for a specific client.  I want all the orders to be tied to their Client Page in commonsku.",
          nextState: "CHECKOUTS",
          bidirectional: true,
        },
        {
          value:
            "I'm creating a Shop for marketing purposes. I want to send it to different clients and prospects so they can place an order.",
          nextState: "MARKETING",
        },
      ],
    },
    CHECKOUTS: {
      id: "CHECKOUTS",
      value: "How would you like to process your Shop's orders?",
      transitions: [
        {
          value: "I'd like to move each order into production right away.",
          nextState: "PAYMENT",
          bidirectional: true,
        },
        {
          value: "I'd like to combine the checkouts into one sales order.",
          nextState: "POPUP",
        },
      ],
    },
    PAYMENT: {
      id: "PAYMENT",
      value: "Will your Shop offer credit card payment?",
      transitions: [
        {
          value: "Yes",
          nextState: "SHIPPING",
          bidirectional: true,
        },
        {
          value: "No",
          nextState: "COMPANY",
        },
      ],
    },
    SHIPPING: {
      id: "SHIPPING",
      value: "Do you know your shoppers' ship-to locations?",
      transitions: [
        {
          value: "Yes",
          nextState: "COMPANY",
        },
        {
          value: "No",
          nextState: "POPUP",
        },
      ],
    },
    MARKETING: {
      id: "MARKETING",
      value: null,
      transitions: [],
    },
    COMPANY: {
      id: "COMPANY",
      value: null,
      transitions: [],
    },
    POPUP: {
      id: "POPUP",
      value: null,
      transitions: [],
    },
    SHOPIFY: {
      id: "SHOPIFY",
      value: null,
      transitions: [],
    },
  },
});

export const shopDetails = {
  POPUP: {
    type: "Pop-Up",
    desc: "Easily collect and combine orders from multiple shoppers while keeping products on-brand and pre-approved. Perfect for short-term events, fundraising, and employee appreciation programs.",
    details: {
      "Great for": "Fundraisers, team events, and seasonal campaigns.",
      "Combine small orders":
        "Merge multiple checkouts to meet product minimums.",
      "On-brand experience": "Pre-approved artwork and pricing.",
      "Flexible pricing":
        "Set retail prices to include additional margin for profit.",
      "Free item programs": "Reward employees or offer zero-dollar items.",
      "Seamless shopping":
        "Supports password protection, payments, coupons & tax calculation.",
      "Custom checkout questions": "Gather extra details from shoppers.",
    },
  },
  COMPANY: {
    type: "Company",
    desc: "Make branded products easily available for employees, franchisees, and partners with pre-approved designs and pricing. Enforces minimum quantities, ensuring every order is production-ready.",
    details: {
      "Great for": "Company uniforms, employee merch, and internal ordering.",
      "Exclusive access": "Designed for employees, franchisees, or partners.",
      "Consistent branding": "Pre-approved artwork and pricing.",
      "Supports inventory & minimums": "Every order generates a sales order.",
      "Secure shopping": "Password-protected with credit card payment options.",
      "Custom checkout questions": "Collect additional order details.",
    },
  },
  MARKETING: {
    type: "Marketing",
    desc: "Showcase products in an e-commerce-style presentation for a broad audience. Shoppers can upload artwork, and minimum quantities ensure clear cost visibility.",
    details: {
      "Great for": "Large-scale marketing campaigns and product promotions.",
      "Broad distribution":
        "Share with your entire client base, not tied to one client.",
      "Full customization": "Shoppers can upload their own artwork.",
      "Transparent pricing":
        "Minimum quantities ensure clear cost expectations.",
      "Seamless production": "Sales orders include full decoration details.",
      "Custom checkout questions": "Gather extra details from shoppers.",
    },
  },
  SHOPIFY: {
    type: "Shopify Connected",
    desc: [
      "Seamlessly connect your commonsku Shop to a Shopify store, allowing orders to flow directly into commonsku for streamlined production and order management. Perfect for distributors selling on Shopify who need a powerful workflow solution.",
      <strong>
        commonsku Shops app available in the Shopify marketplace.
      </strong>,
    ],
    details: {
      "Great for": "Distributors using Shopify for e-commerce sales.",
      "Direct Shopify integration":
        "Connect a commonsku Shop 1:1 to a Shopify store.",
      "Automated order imports":
        "Shopify purchases sync as checkouts in commonsku.",
      "Product SKU mapping":
        "Link commonsku supplier products to Shopify listings.",
      "Pop-up Shop Workflow": "Aggregate Shopify checkouts into sales orders.",
      "Seamless operations":
        "Leverage commonsku’s workflow tools for projects.",
    },
  },
  POD: {
    type: "Print on Demand",
    desc: ["Launch a fully automated online store with no inventory, no minimums, and built-in fulfillment. Perfect for ongoing merch programs, creator stores, employee appreciation, and evergreen brand storefronts for your clients."],
    details: {
      "No inventory required": "Products are printed and shipped only after an order is placed. One item or one hundred — production starts at quantity one.",
      "Expanded product catalogue": "Access a growing network of integrated PoD suppliers that offer a wide variety of products and designs.",
      "Production-ready validation": "Built-in artwork and decoration checks ensure products are ready to sell.",
      "Real-time pricing": "Transparent product, decoration, and shipping costs calculated automatically.",
      "Fully automated fulfillment": "Orders route instantly to our Print on Demand partners for production and shipping.",
      "Always-on revenue": "Keep your shop live year-round without coordinating bulk orders.",
    },
  },
};

export const shopComparison = {
  features: [
    "Inventory",
    "Minimum quantity enforced",
    "Option to show quantity price breaks",
    "Coupons",
    "Upcharges to any product’s size / color variants",
    "Limited to one client",
    "Option to add additional retail mark up",
    "Users can upload artwork",
    "Sales order created upon checkout",
    "Aggregation of orders into a single sales order",
    "User may enter shipping address",
    "Option to add tax at checkout",
    "Custom checkout questions",
    "Product personalization questions",
    "Option to enable credit card checkout",
    "Option to create invoice at checkout",
    "Option to create receipt at checkout",
    "Option to set checkout limit",
  ],
  POPUP: {
    title: "Pop-up Shop",
    features: [
      "Minimum quantity enforced",
      "Coupons",
      "Upcharges to any product’s size / color variants",
      "Limited to one client",
      "Option to add additional retail mark up",
      "Option to add additional retail mark up",
      "Aggregation of orders into a single sales order",
      "Option to add tax at checkout",
      "Custom checkout questions",
      "Product personalization questions",
      "Option to enable credit card checkout",
      "Option to create receipt at checkout",
      "Option to set checkout limit",
    ],
  },
  COMPANY: {
    title: "Company Shop",
    features: [
      "Inventory",
      "Minimum quantity enforced",
      "Option to show quantity price breaks",
      "Upcharges to any product’s size / color variants",
      "Limited to one client",
      "Sales order created upon checkout",
      "User may enter shipping address",
      "Option to add tax at checkout",
      "Custom checkout questions",
      "Option to enable credit card checkout",
      "Option to create invoice at checkout",
    ],
  },
  MARKETING: {
    title: "Marketing Shop",
    features: [
      "Minimum quantity enforced",
      "Option to show quantity price breaks",
      "Upcharges to any product’s size / color variants",
      "Users can upload artwork",
      "Sales order created upon checkout",
      "User may enter shipping address",
      "Custom checkout questions",
    ],
  },
};
